import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { profileApi } from "@/api";
import { auth } from "@/lib/auth";
import { buildLoginRedirect } from "@/lib/navigation";
import { RequestError } from "@/lib/request";

const FEEDBACK_TYPES = [
  { key: "功能建议", icon: "ri-lightbulb-line", color: "#E8860A", bg: "#FFF8E7" },
  { key: "体验问题", icon: "ri-emotion-unhappy-line", color: "#E05C5C", bg: "#FFF5F5" },
  { key: "技师服务", icon: "ri-service-line", color: "#FF6018", bg: "#FFF4EC" },
  { key: "支付问题", icon: "ri-bank-card-line", color: "#17A87B", bg: "#EDFBF4" },
  { key: "其他", icon: "ri-more-line", color: "#9B72CF", bg: "#F3EEFF" },
];

const RATING_ITEMS = [
  { key: "overall", label: "整体体验" },
  { key: "therapist", label: "技师服务" },
  { key: "app", label: "App 使用" },
];

export default function FeedbackPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [feedbackType, setFeedbackType] = useState("功能建议");
  const [ratings, setRatings] = useState<Record<string, number>>({ overall: 0, therapist: 0, app: 0 });
  const [content, setContent] = useState("");
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleRating = (key: string, value: number) => {
    setRatings((prev) => ({ ...prev, [key]: value }));
  };

  const redirectToLogin = () => {
    navigate(buildLoginRedirect(`${location.pathname}${location.search}`), { replace: true });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!auth.getToken()) {
      redirectToLogin();
      return;
    }

    if (!content.trim()) {
      setError("请填写反馈内容");
      return;
    }

    if (content.length > 500) {
      setError("内容不能超过500字");
      return;
    }

    setError("");
    setSubmitting(true);

    try {
      await profileApi.submitFeedback({
        feedbackType,
        content: content.trim(),
        contactEmail: email.trim() || undefined,
        ratingOverall: ratings.overall,
        ratingTherapist: ratings.therapist,
        ratingApp: ratings.app,
      });
      setSubmitted(true);
    } catch (submitError) {
      if (submitError instanceof RequestError && submitError.code === 1001) {
        redirectToLogin();
        return;
      }
      setError("提交失败，请稍后重试");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="flex min-h-screen flex-col pb-8"
      style={{ background: "#F5F4F1", maxWidth: 480, margin: "0 auto" }}
    >
      <div
        className="relative overflow-hidden px-5 pb-6 pt-12"
        style={{ background: "linear-gradient(150deg, #FFB07A 0%, #FF8040 55%, #FF6018 100%)" }}
      >
        <div className="absolute -right-8 -top-8 h-36 w-36 rounded-full" style={{ background: "rgba(255,255,255,0.06)" }} />
        <div className="relative z-10 mb-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full cursor-pointer"
            style={{ background: "rgba(255,255,255,0.2)" }}
          >
            <i className="ri-arrow-left-s-line text-lg text-white" />
          </button>
          <h1 className="flex-1 text-base font-bold text-white">建议反馈</h1>
        </div>
        <div className="relative z-10">
          <p className="mb-0.5 text-sm font-semibold text-white">你的每条反馈，我们都认真对待</p>
          <p className="text-[11px]" style={{ color: "rgba(255,255,255,0.65)" }}>
            帮助我们做得更好，优质反馈将获得优惠券奖励
          </p>
        </div>
      </div>

      {submitted ? (
        <div className="flex flex-1 flex-col items-center justify-center px-5 py-10">
          <div
            className="mb-5 flex h-20 w-20 items-center justify-center rounded-full"
            style={{ background: "linear-gradient(135deg, #FFF4EC, #FFE8CC)" }}
          >
            <i className="ri-check-double-line text-4xl" style={{ color: "#FF6018" }} />
          </div>
          <p className="mb-2 text-lg font-bold" style={{ color: "#1A1A1A" }}>感谢你的反馈！</p>
          <p className="mb-2 text-center text-sm leading-relaxed" style={{ color: "#999" }}>
            我们会认真阅读每一条建议
            <br />
            符合奖励规则的反馈会在审核后补发优惠券
          </p>
          <div
            className="mt-4 flex items-center gap-2 rounded-full px-4 py-2.5"
            style={{ background: "#FFF4EC", border: "1px solid #FFD4A0" }}
          >
            <i className="ri-gift-line text-sm" style={{ color: "#FF6018" }} />
            <span className="text-xs font-semibold" style={{ color: "#FF6018" }}>优质反馈奖励券会在审核后发放至你的账户</span>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="mt-8 whitespace-nowrap rounded-2xl px-8 py-3 text-sm font-bold text-white cursor-pointer"
            style={{ background: "linear-gradient(90deg, #FF5C00, #FF9A3C)" }}
          >
            返回
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex-1 space-y-5 px-5 pb-6 pt-5">
          <div className="space-y-4 rounded-2xl p-4" style={{ background: "#fff", border: "1px solid #F0EAE0" }}>
            <p className="text-xs font-semibold" style={{ color: "#888" }}>评分（可选）</p>
            {RATING_ITEMS.map((item) => (
              <div key={item.key} className="flex items-center justify-between">
                <span className="text-sm" style={{ color: "#555" }}>{item.label}</span>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => handleRating(item.key, star)}
                      className="flex h-7 w-7 items-center justify-center cursor-pointer"
                    >
                      <i
                        className={star <= ratings[item.key] ? "ri-star-fill text-lg" : "ri-star-line text-lg"}
                        style={{ color: star <= ratings[item.key] ? "#FF7B22" : "#DDD0C0" }}
                      />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div>
            <p className="mb-3 text-xs font-semibold" style={{ color: "#888" }}>反馈类型</p>
            <div className="flex flex-wrap gap-2">
              {FEEDBACK_TYPES.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setFeedbackType(item.key)}
                  className="flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-2 text-xs font-semibold cursor-pointer transition-all"
                  style={{
                    background: feedbackType === item.key ? item.bg : "#F5F2EE",
                    color: feedbackType === item.key ? item.color : "#999",
                    border: `1.5px solid ${feedbackType === item.key ? `${item.color}60` : "transparent"}`,
                  }}
                >
                  <i className={`${item.icon} text-sm`} />
                  {item.key}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold" style={{ color: "#888" }}>
              详细描述 <span style={{ color: "#FF5C00" }}>*</span>
            </p>
            <textarea
              name="content"
              value={content}
              onChange={(event) => setContent(event.target.value.slice(0, 500))}
              placeholder="描述你遇到的问题或建议，越详细越好，有助于我们快速改进"
              rows={5}
              className="w-full resize-none rounded-2xl px-4 py-3.5 text-sm leading-relaxed outline-none"
              style={{
                background: "#fff",
                border: `1.5px solid ${error && !content.trim() ? "#FF5C00" : "#EDE8E0"}`,
                color: "#1A1A1A",
              }}
            />
            <div className="mt-1 flex items-center justify-between px-0.5">
              {error ? (
                <p className="text-[10px]" style={{ color: "#FF5C00" }}>{error}</p>
              ) : <span />}
              <p className="text-[10px]" style={{ color: content.length > 480 ? "#FF5C00" : "#C0B8AE" }}>
                {content.length}/500
              </p>
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold" style={{ color: "#888" }}>联系邮箱（可选，方便我们回复）</p>
            <div
              className="flex items-center gap-2 rounded-2xl px-4 py-3"
              style={{ background: "#fff", border: "1px solid #EDE8E0" }}
            >
              <i className="ri-mail-line flex-shrink-0 text-sm" style={{ color: "#C0B8AE" }} />
              <input
                type="email"
                name="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="your@email.com"
                className="flex-1 bg-transparent text-sm outline-none"
                style={{ color: "#1A1A1A" }}
              />
            </div>
          </div>

          <div
            className="flex items-start gap-2.5 rounded-xl p-3.5"
            style={{ background: "#FFF8F3", border: "1px solid #FFE4CC" }}
          >
            <i className="ri-gift-2-line mt-0.5 flex-shrink-0 text-sm" style={{ color: "#FF9A3C" }} />
            <p className="text-[11px] leading-relaxed" style={{ color: "#CC7A30" }}>
              每条有效反馈我们都会认真处理，优质反馈通过审核后将获得 <strong>10-60元优惠券</strong> 奖励，感谢你帮助摩康变得更好！
            </p>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full whitespace-nowrap rounded-2xl py-4 text-sm font-bold text-white cursor-pointer"
            style={{
              background: submitting ? "#E8E0D8" : "linear-gradient(90deg, #FF5C00, #FF9A3C)",
              color: submitting ? "#B0A898" : "#fff",
            }}
          >
            {submitting ? "提交中..." : "提交反馈"}
          </button>
        </form>
      )}
    </div>
  );
}
